import { useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useToast } from "@/hooks/use-toast";
import { Plus, Trash2, Send } from "lucide-react";

const rfpSchema = z.object({
  title: z.string().min(5, "Title must be at least 5 characters"),
  description: z.string().min(20, "Description must be at least 20 characters"),
  category: z.string().optional(),
  budgetRange: z.string().optional(),
  submissionDeadline: z.string().min(1, "Submission deadline is required"),
  enableReverseAuction: z.enum(["yes", "no"]),
  auctionStart: z.string().optional(),
  auctionEnd: z.string().optional(),
  minDecrement: z.string().optional(),
  minBid: z.string().optional()
}).refine(data => {
  if (data.enableReverseAuction === "no") return true;
  return data.auctionStart && data.auctionStart.length > 0;
}, {
  message: "Auction start time is required",
  path: ["auctionStart"]
}).refine(data => {
  if (data.enableReverseAuction === "no") return true;
  return data.auctionEnd && data.auctionEnd.length > 0;
}, {
  message: "Auction end time is required",
  path: ["auctionEnd"]
}).refine(data => {
  if (data.enableReverseAuction === "no" || !data.auctionStart) return true;
  return new Date(data.auctionStart) > new Date(data.submissionDeadline);
}, {
  message: "Auction start must be after submission deadline",
  path: ["auctionStart"]
}).refine(data => {
  if (data.enableReverseAuction === "no" || !data.auctionStart || !data.auctionEnd) return true;
  return new Date(data.auctionEnd) > new Date(data.auctionStart);
}, {
  message: "Auction end must be after auction start",
  path: ["auctionEnd"]
});

type RFPFormValues = z.infer<typeof rfpSchema>;

interface Supplier {
  id: string;
  name: string;
  email: string;
}

const CreateRFP = () => {
  const context = useOutletContext();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [supplierName, setSupplierName] = useState("");
  const [supplierEmail, setSupplierEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (context === "title-only") {
    return "QuickQuote";
  }

  const form = useForm<RFPFormValues>({
    resolver: zodResolver(rfpSchema),
    defaultValues: {
      title: "",
      description: "",
      category: "",
      budgetRange: "",
      submissionDeadline: "",
      enableReverseAuction: "yes",
      auctionStart: "",
      auctionEnd: "",
      minDecrement: "",
      minBid: ""
    }
  });

  const enableReverseAuction = form.watch("enableReverseAuction");
  const addSupplier = () => {
    if (!supplierName || !supplierEmail) {
      toast({
        title: "Error",
        description: "Please enter both supplier name and email",
        variant: "destructive"
      });
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(supplierEmail)) {
      toast({
        title: "Error",
        description: "Please enter a valid email address",
        variant: "destructive"
      });
      return;
    }
    setSuppliers([...suppliers, {
      id: crypto.randomUUID(),
      name: supplierName,
      email: supplierEmail
    }]);
    setSupplierName("");
    setSupplierEmail("");
  };
  const removeSupplier = (id: string) => {
    setSuppliers(suppliers.filter(s => s.id !== id));
  };
  const onSubmit = async (values: RFPFormValues) => {
    if (suppliers.length === 0) {
      toast({
        title: "Error",
        description: "Please add at least one supplier",
        variant: "destructive"
      });
      return;
    }
    setIsSubmitting(true);
    try {
      const {
        data: {
          user
        }
      } = await supabase.auth.getUser();
      if (!user) {
        navigate("/signin");
        return;
      }

      // Create RFP
      const {
        data: rfp,
        error: rfpError
      } = await supabase.from("rfps").insert({
        buyer_id: user.id,
        title: values.title,
        description: values.description,
        category: values.category || null,
        budget_range: values.budgetRange || null,
        submission_deadline: new Date(values.submissionDeadline).toISOString(),
        auction_start: values.enableReverseAuction === "yes" && values.auctionStart 
          ? new Date(values.auctionStart).toISOString() 
          : new Date(values.submissionDeadline).toISOString(),
        auction_end: values.enableReverseAuction === "yes" && values.auctionEnd 
          ? new Date(values.auctionEnd).toISOString() 
          : new Date(values.submissionDeadline).toISOString(),
        min_decrement: values.enableReverseAuction === "yes" && values.minDecrement ? parseFloat(values.minDecrement) : null,
        min_bid: values.enableReverseAuction === "yes" && values.minBid ? parseFloat(values.minBid) : null,
        status: "OPEN_FOR_SUBMISSIONS"
      }).select().single();
      if (rfpError) throw rfpError;

      // Create supplier invitations
      const invitations = suppliers.map(supplier => ({
        rfp_id: rfp.id,
        supplier_name: supplier.name,
        supplier_email: supplier.email,
        invite_token: crypto.randomUUID()
      }));
      const {
        data: createdInvitations,
        error: invitationsError
      } = await supabase.from("supplier_invitations").insert(invitations).select();
      if (invitationsError) throw invitationsError;

      // Get buyer profile
      const {
        data: profile
      } = await supabase.from("profiles").select("name").eq("id", user.id).single();

      // Send invitation emails
      const {
        error: emailError
      } = await supabase.functions.invoke("send-rfp-invitations", {
        body: {
          rfpId: rfp.id,
          invitations: createdInvitations.map(inv => ({
            id: inv.id,
            supplierName: inv.supplier_name,
            supplierEmail: inv.supplier_email,
            inviteToken: inv.invite_token
          })),
          rfpTitle: rfp.title,
          rfpDescription: rfp.description,
          submissionDeadline: rfp.submission_deadline,
          auctionStart: rfp.auction_start,
          auctionEnd: rfp.auction_end,
          buyerName: profile?.name || "A buyer"
        }
      });
      if (emailError) {
        console.error("Email error:", emailError);
        toast({
          title: "Warning",
          description: "RFP created but some invitation emails may not have been sent"
        });
      } else {
        toast({
          title: "Success",
          description: `RFP created and ${suppliers.length} invitations sent!`
        });
      }
      navigate("/auctions");
    } catch (error: any) {
      console.error("Error creating RFP:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to create RFP. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  return <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Create QuickQuote</h2>
        <p className="text-gray-600 mt-1">Issue instructions or an RFP quickly, to receive quotes. Great for matters that need to be instructed on promptly.</p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>RFP Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField control={form.control} name="title" render={({
              field
            }) => <FormItem>
                    <FormLabel>RFP Title *</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter RFP title" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />

              <FormField control={form.control} name="description" render={({
              field
            }) => <FormItem>
                    <FormLabel>Description *</FormLabel>
                    <FormControl>
                      <Textarea placeholder="Describe your requirements" className="min-h-[120px]" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField control={form.control} name="category" render={({
                field
              }) => <FormItem>
                      <FormLabel>Category</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g., IT Services" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />

                <FormField control={form.control} name="budgetRange" render={({
                field
              }) => <FormItem>
                      <FormLabel>Budget Range</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g., $50,000 - $100,000" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Timeline</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField control={form.control} name="submissionDeadline" render={({
              field
            }) => <FormItem>
                    <FormLabel>Submission Deadline *</FormLabel>
                    <FormControl>
                      <Input type="datetime-local" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />

              <FormField
                control={form.control}
                name="enableReverseAuction"
                render={({ field }) => (
                  <FormItem className="space-y-3">
                    <FormLabel>Enable Reverse Auction</FormLabel>
                    <FormDescription>
                      Allow vendors to bid against each other in real-time
                    </FormDescription>
                    <FormControl>
                      <RadioGroup
                        onValueChange={field.onChange}
                        value={field.value}
                        className="flex space-x-4"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="yes" id="auction-yes" />
                          <label htmlFor="auction-yes" className="text-sm cursor-pointer">Yes</label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="no" id="auction-no" />
                          <label htmlFor="auction-no" className="text-sm cursor-pointer">No</label>
                        </div>
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {enableReverseAuction === "yes" && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField control={form.control} name="auctionStart" render={({
                    field
                  }) => <FormItem>
                          <FormLabel>Auction Start *</FormLabel>
                          <FormControl>
                            <Input type="datetime-local" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>} />

                    <FormField control={form.control} name="auctionEnd" render={({
                    field
                  }) => <FormItem>
                          <FormLabel>Auction End *</FormLabel>
                          <FormControl>
                            <Input type="datetime-local" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>} />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField control={form.control} name="minDecrement" render={({
                    field
                  }) => <FormItem>
                          <FormLabel>Minimum Bid Decrement ($)</FormLabel>
                          <FormControl>
                            <Input type="number" step="0.01" placeholder="e.g., 100" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>} />

                    <FormField control={form.control} name="minBid" render={({
                    field
                  }) => <FormItem>
                          <FormLabel>Minimum Allowed Bid ($)</FormLabel>
                          <FormControl>
                            <Input type="number" step="0.01" placeholder="e.g., 10000" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>} />
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Invite Suppliers</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input placeholder="Supplier name" value={supplierName} onChange={e => setSupplierName(e.target.value)} onKeyPress={e => e.key === "Enter" && (e.preventDefault(), addSupplier())} />
                <Input placeholder="Supplier email" type="email" value={supplierEmail} onChange={e => setSupplierEmail(e.target.value)} onKeyPress={e => e.key === "Enter" && (e.preventDefault(), addSupplier())} />
                <Button type="button" onClick={addSupplier} variant="outline">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>

              {suppliers.length > 0 && <div className="space-y-2">
                  <p className="text-sm font-medium">Added Suppliers ({suppliers.length})</p>
                  {suppliers.map(supplier => <div key={supplier.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                      <div>
                        <p className="font-medium">{supplier.name}</p>
                        <p className="text-sm text-gray-600">{supplier.email}</p>
                      </div>
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeSupplier(supplier.id)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>)}
                </div>}
            </CardContent>
          </Card>

          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => navigate("/auctions")} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? <>Creating...</> : <>
                  <Send className="h-4 w-4 mr-2" />
                  Create & Send Invitations
                </>}
            </Button>
          </div>
        </form>
      </Form>
    </div>;
};
export default CreateRFP;